// ContactModal.jsx
import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { FaTimes, FaPaperPlane } from "react-icons/fa";
import { gradients } from "../theme/theme";
import { db } from "../firebase/firebaseConfig";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";

/* --- Animations --- */
const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95) }
  to { opacity: 1; transform: scale(1) }
`;
const slideUp = keyframes`
  from { transform: translateY(40px); opacity: 0 }
  to { transform: translateY(0); opacity: 1 }
`;

/* --- Overlay --- */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(6px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  animation: ${fadeIn} 0.25s ease;
`;

/* --- Modal Box --- */
const ModalBox = styled.div`
  background: ${gradients.heroBackground};
  border-radius: 18px;
  padding: 2rem;
  width: 100%;
  max-width: 420px;
  color: #fff;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: ${slideUp} 0.3s ease;
  position: relative;
`;

/* --- Close Button --- */
const CloseBtn = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  background: rgba(255, 255, 255, 0.08);
  border: none;
  padding: 8px;
  border-radius: 50%;
  color: #fff;
  font-size: 1rem;
  cursor: pointer;
  transition: 0.25s;
  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
`;

/* --- Title --- */
const Title = styled.h2`
  margin-top: 0;
  margin-bottom: 1rem;
  font-size: 1.8rem;
  background: linear-gradient(90deg, #ff7a18, #ff0066);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
`;

/* --- Input Fields Wrapper --- */
const InputGroup = styled.div`
  margin-bottom: 1rem;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const FieldStyles = `
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  border: none;
  outline: none;
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  font-size: 0.95rem;
  display: block;
  margin: 0 auto;
  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const Input = styled.input`
  ${FieldStyles}
`;
const Select = styled.select`
  ${FieldStyles}
  option {
    background: #1a1a1a;
    color: #fff;
  }
`;
const TextArea = styled.textarea`
  ${FieldStyles} resize: none;
`;

/* --- Submit Button --- */
const SubmitBtn = styled.button`
  width: 100%;
  padding: 0.8rem;
  border-radius: 12px;
  background: linear-gradient(90deg, #ff7a18, #ff0066);
  border: none;
  font-size: 1rem;
  color: #fff;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.25s, box-shadow 0.25s;
  display: block;
  margin: 0 auto;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(255, 0, 102, 0.4);
  }
`;

export default function ContactModal({
  isOpen,
  onClose,
  modalType = "quote",
  preselectedService = "",
}) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    service: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  // ✅ Auto-preselect dropdown when modal opens
  useEffect(() => {
    if (preselectedService) {
      setFormData((prev) => ({ ...prev, service: preselectedService }));
    }
  }, [preselectedService]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addDoc(collection(db, "quotations"), {
        ...formData,
        modalType,
        createdAt: serverTimestamp(),
      });
      alert("✅ Request sent successfully!");
      setFormData({
        name: "",
        email: "",
        mobile: "",
        service: "",
        message: "",
      });
      onClose();
    } catch (error) {
      console.error("Error sending request:", error);
      alert("❌ Failed to send request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay>
      <ModalBox>
        <CloseBtn onClick={onClose}>
          <FaTimes />
        </CloseBtn>
        <Title>
          {modalType === "mentorship"
            ? "Ask for Mentorship"
            : "Request a Quote"}
        </Title>

        <form onSubmit={handleSubmit}>
          <InputGroup>
            <Input
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </InputGroup>
          <InputGroup>
            <Input
              type="email"
              name="email"
              placeholder="Your Email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </InputGroup>
          <InputGroup>
            <Input
              type="tel"
              name="mobile"
              placeholder="Your Mobile"
              value={formData.mobile}
              onChange={handleChange}
              required
            />
          </InputGroup>
          <InputGroup>
            <Select
              name="service"
              value={formData.service}
              onChange={handleChange}
              required
            >
              <option value="">Select a Service</option>
              <option value="mobile">📱 Mobile App Development</option>
              <option value="web">💻 Web Development</option>
              <option value="backend">🔗 Backend / API Development</option>
              <option value="fullstack">⚡ Full Stack Solutions</option>
              <option value="courses">🎓 Mentorship</option>
              <option value="career">🚀 Portfolio & Career Support</option>
            </Select>
          </InputGroup>
          <InputGroup>
            <TextArea
              rows="4"
              name="message"
              placeholder="Your Message"
              value={formData.message}
              onChange={handleChange}
              required
            ></TextArea>
          </InputGroup>
          <SubmitBtn type="submit" disabled={loading}>
            <FaPaperPlane style={{ marginRight: "6px" }} />
            {loading ? "Sending..." : "Send Request"}
          </SubmitBtn>
        </form>
      </ModalBox>
    </Overlay>
  );
}
